import { useState } from 'react';
import { Loader2, Play, Plug2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Verdict({ pass: ok }) {
  if (ok) {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1 text-[10px]">
        <CheckCircle2 className="h-3 w-3" />
        PASS
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1 text-[10px]">
      <XCircle className="h-3 w-3" />
      FAIL
    </Badge>
  );
}

function EnvRow({ name, info }) {
  return (
    <tr className="border-b last:border-0">
      <td className="px-3 py-1.5 font-mono text-xs font-medium">{name}</td>
      <td className="px-3 py-1.5 text-xs">
        {info.present ? (
          <span className="text-green-600 dark:text-green-400">present</span>
        ) : (
          <span className="text-red-500 dark:text-red-400">missing</span>
        )}
      </td>
      <td className="px-3 py-1.5 text-xs text-muted-foreground font-mono">
        {info.last4 || (info.length ? `length=${info.length}` : info.value || info.hint || '')}
      </td>
    </tr>
  );
}

export function BridgeConnectDryrunPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.bridgeConnectDryrun();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Plug2 className="h-5 w-5 text-blue-600" />
            Bridge Connect Dry-Run
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Run
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Pre-flight check for Bridge (BNP) Connect. Verifies env vars, callback URL, and credentials against the Bridge API. Read-only, no side effects.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-4">
          {/* Overall banner */}
          <div className={`border rounded-lg p-3 ${result.overall_pass ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center gap-2 text-sm font-medium">
              {result.overall_pass ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-green-700 dark:text-green-400">Bridge Connect ready</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="text-red-700 dark:text-red-400">Bridge Connect will fail (see details below)</span>
                </>
              )}
            </div>
          </div>

          {/* Env vars table */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
              Environment Variables
            </h4>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-1.5 font-medium">Variable</th>
                    <th className="text-left px-3 py-1.5 font-medium">Status</th>
                    <th className="text-left px-3 py-1.5 font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {result.env_vars && Object.entries(result.env_vars).map(([name, info]) => (
                    <EnvRow key={name} name={name} info={info} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Callback URL */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
              Callback URL
            </h4>
            <div className="border rounded-lg p-3 space-y-1 text-xs">
              <div>
                <span className="text-muted-foreground">Configured: </span>
                <span className="font-mono">{result.callback_url?.configured_url}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Would send to Bridge: </span>
                <span className="font-mono">{result.callback_url?.would_send}</span>
              </div>
            </div>
          </div>

          {/* Credential check */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
              Live Credential Check
            </h4>
            <div className="border rounded-lg p-3 space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <Verdict pass={result.credential_check?.pass} />
                <span>{result.credential_check?.detail}</span>
              </div>
              {result.credential_check?.probe_url && (
                <div>
                  <span className="text-muted-foreground">Probe URL: </span>
                  <span className="font-mono">{result.credential_check.probe_url}</span>
                </div>
              )}
              {result.credential_check?.upstream_status && !result.credential_check?.pass && (
                <div className="text-red-600 dark:text-red-400">
                  <span className="font-medium">Upstream status: </span>
                  <span className="font-mono">{result.credential_check.upstream_status}</span>
                </div>
              )}
              {result.credential_check?.upstream_body && (
                <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
                  {result.credential_check.upstream_body}
                </pre>
              )}
            </div>
          </div>

          {result.checked_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Checked at {new Date(result.checked_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
